// Persistence + defaults for the theme switcher. Lives outside ThemeContext.tsx
// so it can be unit-tested without standing up a React tree.

import type { ThemeId, ColorMode } from './themeTypes';
import { THEME_IDS } from './themeTypes';

export const THEME_STORAGE_KEY = 'ct_v3_theme_prefs';
/**
 * v1 key carried over from the cloned Image Lab UI. We read it once on first
 * load (when the new key isn't yet populated) and migrate, then never look at
 * it again. Drop once we're confident no live env has the old entry.
 */
export const LEGACY_THEME_STORAGE_KEY = 'smartai-imagelab-theme-prefs';

export interface StoredPrefs {
  themeId: ThemeId;
  colorMode: ColorMode;
}

/**
 * Bump when a rebrand should reach people who already chose a theme.
 *
 * A stored preference normally outranks the default — otherwise the switcher
 * would be pointless, reverting on every reload. But a preference saved before
 * a rebrand reflects a decision about the *old* identity, so carrying it forward
 * hides the new one from exactly the people who have used the product most.
 *
 * Prefs written under an older epoch (or none — everything before this existed)
 * are discarded in favour of DEFAULT_PREFS. A theme picked after the bump is
 * stamped with the current epoch and persists normally.
 *
 * Only a deliberate choice may write this stamp. `ThemeManagerProvider` skips
 * its persist effect on mount for that reason — echoing the seeded state back
 * would relabel a pre-rebrand preference as a post-rebrand one, and the swap
 * would never happen. Epoch 3 retires entries that epoch 2 mis-stamped that way.
 */
export const PREFS_EPOCH = 3;

interface VersionedPrefs extends StoredPrefs {
  epoch?: number;
}

/**
 * architectureDesign.md §6 — the Coral rebrand moves the fresh-visitor default.
 *
 * Reef Modernism is the product's own visual identity, so it greets anyone who
 * hasn't chosen otherwise, in **light** — its reference mode, the sand-lit key
 * the palette was drawn in. This supersedes the earlier split default (labs →
 * `smartai` dark, hub → `studio`), which predates the rebrand.
 *
 * This takes precedence over any preference stored before PREFS_EPOCH, so the
 * rebrand reaches returning visitors rather than only new ones. A theme chosen
 * after the bump persists as usual, and `smartai`, `studio` and `constellations`
 * all remain registered and one click away.
 */
export const DEFAULT_PREFS: StoredPrefs = {
  themeId: 'reef',
  colorMode: 'light',
};

export type SurfaceKind = 'labs' | 'hub';

/**
 * Seed theme for a surface when nothing is persisted. Both surfaces now land on
 * `reef`: the rebrand gives the hub and the labs one identity rather than two.
 *
 * The seam is kept (rather than collapsed into `DEFAULT_PREFS`) because planned
 * work still wants to vary the *fallback* per surface — `ThemeManagerProvider`
 * takes it as `initialThemeId`, and a stored preference outranks it either way.
 */
export function defaultThemeForSurface(_surface: SurfaceKind): ThemeId {
  return 'reef';
}

function isValidThemeId(value: unknown): value is ThemeId {
  return typeof value === 'string' && (THEME_IDS as readonly string[]).includes(value);
}

function isValidColorMode(value: unknown): value is ColorMode {
  return value === 'dark' || value === 'light';
}

function readStorage(key: string): Partial<VersionedPrefs> | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<VersionedPrefs>;
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

function sanitize(parsed: Partial<VersionedPrefs> | null, fallback: StoredPrefs): StoredPrefs {
  if (!parsed) return fallback;
  return {
    themeId: isValidThemeId(parsed.themeId) ? parsed.themeId : fallback.themeId,
    colorMode: isValidColorMode(parsed.colorMode) ? parsed.colorMode : fallback.colorMode,
  };
}

/** A preference is only honoured if it was saved under the current epoch. */
function isCurrentEpoch(parsed: Partial<VersionedPrefs> | null): boolean {
  return parsed?.epoch === PREFS_EPOCH;
}

export function loadPrefs(fallback: StoredPrefs = DEFAULT_PREFS): StoredPrefs {
  const fresh = readStorage(THEME_STORAGE_KEY);
  if (fresh) {
    // Pre-rebrand choice: replace it with the new default and re-stamp, so this
    // happens once rather than on every load.
    if (!isCurrentEpoch(fresh)) {
      savePrefs(fallback);
      return fallback;
    }
    return sanitize(fresh, fallback);
  }

  // The legacy key predates epochs entirely, so anything there is by definition
  // a pre-rebrand choice. Take the default rather than migrating it forward.
  const legacy = readStorage(LEGACY_THEME_STORAGE_KEY);
  if (legacy) {
    savePrefs(fallback);
    return fallback;
  }

  return fallback;
}

export function savePrefs(prefs: StoredPrefs): void {
  try {
    // Stamped so a later rebrand can tell this choice from an older one.
    const versioned: VersionedPrefs = { ...prefs, epoch: PREFS_EPOCH };
    localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify(versioned));
  } catch {
    // Storage may be full or disabled (private mode); silently degrade —
    // the UI still respects the in-memory selection for the session.
  }
}
